import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Tooltip } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { useTranslation } from "react-i18next";
import type { EditingTab } from "../profilePanel.contract";
import {
  Footer,
  LockedEditButton,
  LockedEditTooltip,
  LockedEditWrap,
  Message,
} from "./profilePanel.styled";

interface ProfilePanelFooterProps {
  saveError: string;
  isEditLocked: boolean;
  canEdit: boolean;
  showEditButton: boolean;
  canSaveProfileEdit: boolean;
  isProfilePhoneNumberValid: boolean;
  showLockedEditHint: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  editingTab: EditingTab;
  showAddEmploymentButton: boolean;
  showDeleteButton: boolean;
  onSetLockedEditHint: (value: boolean) => void;
  onEditClick: () => void;
  onAddEmploymentClick: () => void;
  onDeleteClick: () => void;
  onCancelEdit: () => void;
  onOpenSaveModal: () => void;
}

const DELETE_BUTTON_STYLE_OVERRIDES = {
  textColor: ColorV2.NegativeRed,
  borderColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: "#fce9e9",
  activeBackgroundColor: "rgba(211, 78, 78, 0.2)",
};

/**
 * Handles profile panel footer.
 */
export const ProfilePanelFooter = (
  props: ProfilePanelFooterProps,
): JSX.Element => {
  const { t } = useTranslation();
  const deleteButton = props.showDeleteButton ? (
    <SecondaryButton
      label={t("Staff.ProfilePanelDelete")}
      iconName="v2/trash"
      onClick={props.onDeleteClick}
      disabled={props.isDeleting}
      styleOverrides={DELETE_BUTTON_STYLE_OVERRIDES}
    />
  ) : null;

  return (
    <Footer>
      {props.saveError.length > 0 && <Message>{props.saveError}</Message>}
      {!props.canEdit ? null : props.isEditLocked ? (
        <>
          {deleteButton}
          {props.showEditButton && (
            <LockedEditWrap
              onMouseEnter={() => props.onSetLockedEditHint(true)}
              onMouseLeave={() => props.onSetLockedEditHint(false)}
            >
              {props.showLockedEditHint && (
                <LockedEditTooltip>
                  <Tooltip
                    text={t("Staff.ProfilePanelEditLockedInfo")}
                    arrow="down"
                  />
                </LockedEditTooltip>
              )}
              <LockedEditButton
                label={t("Staff.ProfilePanelEdit")}
                iconName="lock"
                size="large"
                disabled
              />
            </LockedEditWrap>
          )}
        </>
      ) : (
        <>
          {props.editingTab != null ? (
            <>
              <SecondaryButton
                label={t("General.Cancel")}
                onClick={props.onCancelEdit}
                disabled={props.isSaving}
              />
              {(props.editingTab === "employmentSalary" ||
                (props.editingTab === "profile" &&
                  props.canSaveProfileEdit)) && (
                <PrimaryButton
                  label={t("General.Save")}
                  onClick={props.onOpenSaveModal}
                  disabled={
                    props.isSaving ||
                    (props.editingTab === "profile" &&
                      !props.isProfilePhoneNumberValid)
                  }
                />
              )}
            </>
          ) : (
            <>
              {props.showAddEmploymentButton && (
                <SecondaryButton
                  label={t("Staff.ProfilePanelNewAgreement")}
                  iconName="v2/plus"
                  onClick={props.onAddEmploymentClick}
                  disabled={props.isDeleting}
                />
              )}
              {deleteButton}
              {props.showEditButton && (
                <SecondaryButton
                  label={t("Staff.ProfilePanelEdit")}
                  iconName="v2/pen"
                  onClick={props.onEditClick}
                  disabled={props.isDeleting}
                />
              )}
            </>
          )}
        </>
      )}
    </Footer>
  );
};
